<template>
  <div class="optix-lab-analyzer">
    <div class="step-progress-bar mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="font-weight-bold mb-1">
            {{ stepTitle }}
          </h5>
          <p
            v-if="currentStep === 1"
            class="text-muted mb-0"
          >
            Test content with A/B comparisons and audience-specific analysis
          </p>
        </div>
        <div class="step-indicators">
          <span
            v-for="n in 3"
            :key="n"
            class="step-dot"
            :class="{ 'active': n === currentStep, 'completed': n < currentStep }"
          />
        </div>
      </div>
    </div>

    <div
      v-if="currentStep === 1"
      class="step-content"
    >
      <b-form-group>
        <template #label>
          <div class="d-flex align-items-center gap-2">
            <span>Analysis Type:</span>
          </div>
        </template>

        <Multiselect
          v-model="selectedPrompt"
          label="name"
          track-by="id"
          :allow-empty="false"
          :options="promptOptions"
          :disabled="customPrompts.length === 0 || customPrompts[0] === 'loading'"
        />
      </b-form-group>

      <div class="mt-4">
        <b-button
          variant="primary"
          :disabled="!selectedPrompt"
          @click="nextStep"
        >
          Next <i class="fas fa-arrow-right ml-2" />
        </b-button>
      </div>
    </div>

    <div
      v-else-if="currentStep === 2"
      class="step-content"
    >
      <div
        v-if="isVisualPlayBookAnalysis"
        class="visual-playbook-section"
      >
        <div class="rounded border p-3 mb-3">
          <b-form-group label="Select Input Type:">
            <b-form-radio-group
              v-model="inputMode"
              :options="[
                { value: 'search', text: 'Search Term' },
                { value: 'category', text: 'Category URL' },
                { value: 'products', text: 'Specific Product URLs' }
              ]"
              buttons
              button-variant="outline-primary"
              class="w-100"
            />
          </b-form-group>
        </div>

        <div
          v-if="inputMode === 'search'"
          class="rounded border d-flex p-3 mb-3 align-items-center gap-2"
        >
          <p class="text-nowrap mb-0 mr-2">
            Enter search keyword
          </p>
          <b-form-input
            v-model="searchInput"
            placeholder="Enter search keyword"
          />
        </div>

        <div
          v-else-if="inputMode === 'category'"
          class="rounded border p-3 mb-3"
        >
          <p class="mb-2">
            Enter Category URLs
          </p>
          <b-form-textarea
            ref="categoryUrlsTextarea"
            v-model="categoryUrlsText"
            placeholder="Enter one category URL per line"
            rows="3"
            @input="handleCategoryUrlsInput"
            @keyup="handleCategoryUrlsInput"
            @change="handleCategoryUrlsInput"
            @paste="handleCategoryUrlsPaste"
          />
          <small class="text-muted mt-1 d-block">
            {{ categoryUrls.length }} URLs entered
          </small>
        </div>

        <div
          v-else-if="inputMode === 'products'"
          class="rounded border p-3 mb-3"
        >
          <p class="mb-2">
            Enter Product URLs or ASINs (one per line, max 5)
          </p>
          <b-form-textarea
            ref="productUrlsTextarea"
            v-model="productUrlsText"
            placeholder="Enter one URL or ASIN per line (max 5)"
            rows="5"
            @input="handleProductUrlsInput"
            @keyup="handleProductUrlsInput"
            @change="handleProductUrlsInput"
            @paste="handleProductUrlsPaste"
          />
          <small class="text-muted mt-1 d-block">
            {{ productUrls.length }}/5 items entered
            <span
              v-if="allProductUrls.length > 5"
              class="text-danger"
            > - Too many items! Only first 5 will be used.</span>
          </small>
        </div>

        <div
          v-if="inputMode !== 'products'"
          class="rounded border d-flex p-3 mb-3 align-items-center gap-2"
        >
          <p class="text-nowrap mb-0 mr-2">
            Number of processed results
          </p>
          <b-form-input
            v-model="countResults"
            placeholder="Number of processed results"
          />
        </div>

        <div class="rounded border d-flex p-3 mb-3 align-items-center gap-2">
          <p class="text-nowrap mb-0 mr-2">
            Max Images Per Product
          </p>
          <b-form-input
            v-model="maxImagesPerProduct"
            placeholder="Number of max images per product"
          />
        </div>

        <div
          v-if="inputMode === 'search' || inputMode === 'category'"
          class="rounded border d-flex p-3 mb-3 align-items-center gap-2"
        >
          <b-form-group label="Retailers:">
            <b-form-select
              v-model="selectedRetailer"
              :options="retailers"
              value-field="id"
              text-field="name"
            >
              <template #first>
                <b-form-select-option :value="null">
                  Select a retailer
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </div>
      </div>

      <b-tabs
        v-else
        v-model="activeTab"
        content-class="mt-3"
      >
        <b-tab
          title="Upload Files"
          :active="activeTab === 0"
        >
          <div
            ref="uploadContainer"
            tabindex="0"
            class="upload-container"
          >
            <p class="text-center mb-3">
              Upload Images or Choose from File Browser
            </p>

            <div class="paste-instruction">
              <b-icon icon="clipboard" />
              <span>Paste image(s) from clipboard (Ctrl+V)</span>
            </div>

            <div
              v-if="uploadedFilesCount > 0"
              class="upload-count"
            >
              <b-badge variant="success">
                {{ uploadedFilesCount }} file(s) uploaded
              </b-badge>
            </div>

            <div
              class="dropzone-container"
              :class="{ 'loading': isLoadingFileBrowserFiles }"
            >
              <VueDropzone
                id="imageUploadDropzone"
                ref="fileDropzone"
                key="imageUploadDropzone"
                :options="dropzoneOptions"
                @vdropzone-file-added="onFileAdded"
                @vdropzone-removed-file="onFileRemoved"
              />

              <div
                v-if="isLoadingFileBrowserFiles"
                class="file-browser-loading-overlay"
              >
                <div class="loading-content">
                  <b-spinner class="mr-2" />
                  <span>Loading selected files...</span>
                </div>
              </div>
            </div>

            <b-button
              class="mt-2"
              size="sm"
              variant="outline-secondary"
              @click="openSelectFolderModal('default')"
            >
              <b-icon
                icon="folder"
                class="mr-1"
              />
              File browser
            </b-button>
          </div>
        </b-tab>

        <b-tab
          title="URLs/ASINs"
          :active="activeTab === 1"
        >
          <div class="url-input-section">
            <b-form-group
              label="Enter URL, ASIN, or TCIN"
              description="You can enter multiple items separated by spaces or line breaks"
            >
              <b-form-textarea
                v-model="urlInput"
                placeholder="Enter URLs, ASINs, or TCINs (one per line or space-separated)"
                rows="6"
              />
            </b-form-group>
          </div>
        </b-tab>

        <b-tab
          title="Quick Start"
          :active="activeTab === 2"
        >
          <div class="quick-start-section">
            <div class="quick-start-options mb-3">
              <b-button
                variant="outline-secondary"
                class="mb-2 mr-2"
                :disabled="isLoadingPreviousImages"
                @click.prevent="usePreviousImages(0)"
              >
                <i class="fas fa-history mr-2" />
                Use Previous Images
                <b-spinner
                  v-if="isLoadingPreviousImages"
                  small
                  class="ml-2"
                />
              </b-button>

              <b-button
                variant="outline-secondary"
                class="mb-2 mr-2"
                :disabled="isLoadingPreviousImages"
                @click.prevent="usePreviousImages(1)"
              >
                <i class="fas fa-link mr-2" />
                Use Previous ASIN/URL
                <b-spinner
                  v-if="isLoadingPreviousImages"
                  small
                  class="ml-2"
                />
              </b-button>
            </div>

            <div class="product-list-section border rounded p-3">
              <div class="row">
                <div class="col-md-6">
                  <b-form-group label="Select from Product List:">
                    <Multiselect
                      v-model="selectedProductLists"
                      :options="productListOptions"
                      :multiple="true"
                      :searchable="true"
                      :loading="isLoadingProductLists"
                      :disabled="isLoadingProductLists"
                      track-by="id"
                      label="name"
                      placeholder="Choose product lists"
                      @input="loadProductsFromLists"
                    />
                  </b-form-group>
                </div>

                <div
                  v-if="availableProducts.length > 0"
                  class="col-md-6"
                >
                  <b-form-group label="Select Individual Products:">
                    <Multiselect
                      v-model="selectedProducts"
                      :options="availableProducts"
                      :multiple="true"
                      :searchable="true"
                      :disabled="isLoadingProducts"
                      :loading="isLoadingProducts"
                      :close-on-select="false"
                      track-by="id"
                      label="display_name"
                      placeholder="Select specific products"
                      :allow-empty="true"
                      :limit="3"
                      :limit-text="limitText"
                      :max-height="300"
                      @input="populateSelectedProducts"
                    >
                      <template
                        slot="option"
                        slot-scope="props"
                      >
                        <div class="multiselect-option">
                          <strong>{{ props.option.display_name }}</strong>
                          <small class="text-muted d-block">{{ props.option.product_list_name }}</small>
                        </div>
                      </template>
                      <template slot="noResult">
                        No products found
                      </template>
                    </Multiselect>
                    <div class="mt-2">
                      <b-button
                        :variant="selectedProducts.length === availableProducts.length ? 'primary' : 'outline-primary'"
                        size="sm"
                        class="mr-2"
                        :disabled="isLoadingProducts || availableProducts.length === 0"
                        @click="selectAll"
                      >
                        <b-icon :icon="selectedProducts.length === availableProducts.length ? 'check-square' : 'square'" /> Select All
                      </b-button>
                      <b-button
                        v-if="selectedProducts.length > 0"
                        variant="outline-danger"
                        size="sm"
                        @click="clearAll"
                      >
                        <i class="fas fa-times" /> Clear All
                      </b-button>
                    </div>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <div class="mt-4 d-flex justify-content-between">
        <b-button
          variant="secondary"
          @click="previousStep"
        >
          <i class="fas fa-arrow-left mr-2" /> Back
        </b-button>
        <b-button
          variant="success"
          size="lg"
          :disabled="!canGenerateReport || isGenerating"
          @click="debugGenerateReport"
        >
          <b-spinner
            v-if="isGenerating"
            small
            class="mr-2"
          />
          <i
            v-else
            class="fas fa-play mr-2"
          />
          {{ isGenerating ? 'Generating...' : 'Generate Report' }}
        </b-button>
      </div>
    </div>

    <div
      v-else-if="currentStep === 3"
      class="step-content"
    >
      <div class="text-center">
        <div class="mb-4">
          <div class="generating-spinner mb-3">
            <b-spinner
              variant="primary"
              style="width: 4rem; height: 4rem;"
            />
          </div>
          <h4 class="mb-3">
            Analyzing image(s)
          </h4>
        </div>
      </div>
    </div>

    <b-overlay
      :show="isLoading"
      rounded
      opacity="0.6"
      spinner-variant="primary"
    >
      <template #overlay>
        <div class="text-center p-4">
          <b-spinner label="Loading..." />
          <p class="mt-2">
            {{ loadingMessage }}
          </p>
        </div>
      </template>
    </b-overlay>

    <SelectFolderModal
      :mode="selectFolderMode"
      @onSelectedItems="fileBrowserItemSelected"
    />
  </div>
</template>

<script>
import VueDropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import Multiselect from 'vue-multiselect'
import SelectFolderModal from './modules/file-browser/modals/SelectFolderModal.vue'
import { dataURLtoFile } from '@frontend/services/helpers'
import axios from 'axios'
import Vue from 'vue'

export default {
  name: 'OptixLabAnalyzer',
  components: {
    VueDropzone,
    Multiselect,
    SelectFolderModal
  },
  provide() {
    return {
      $route: { params: { tab: 'files' } }
    }
  },
  data() {
    return {
      currentStep: 1,
      activeTab: 0,

      selectedPrompt: null,
      customPrompts: ['loading'],

      uploadedFiles: [],
      urlInput: '',
      selectFolderMode: 'default',
      isLoadingFileBrowserFiles: false,
      isLoadingPreviousImages: false,

      inputMode: 'search',
      searchInput: '',
      searchKeyword: '',
      countResults: 4,
      productUrls: [''],
      productUrlsText: '',
      isUpdatingFromTextarea: false,
      allProductUrls: [],
      categoryUrls: [''],
      categoryUrlsText: '',
      isUpdatingCategoryFromTextarea: false,
      maxImagesPerProduct: 7,
      selectedRetailer: null,
      retailers: [
        {
          id: 'amazon',
          name: 'Amazon'
        },
        {
          id: 'walmart',
          name: 'Walmart'
        },
        {
          id: 'target',
          name: 'Target'
        }
      ],
      selectedProductLists: [],
      availableProductLists: [],
      availableProducts: [],
      selectedProducts: [],
      isLoadingProducts: false,
      isLoadingProductLists: false,
      selectedProductRecordIds: [],

      isGenerating: false,
      generationStep: 1,
      generationStatus: 'Processing your request...',
      generationMessage: 'Please wait while we prepare your analysis',

      isLoading: false,
      loadingMessage: '',

      dropzoneOptions: {
        url: "http",
        autoProcessQueue: false,
        addRemoveLinks: true,
        createImageThumbnails: true,
        acceptedFiles: "image/jpeg, image/png, image/gif, image/webp",
        maxFiles: null,
        parallelUploads: 10,
        uploadMultiple: true,
        maxThumbnailFilesize: 50,
        thumbnailWidth: 150,
        thumbnailHeight: 150,
        dictDefaultMessage: 'Drag & Drop your files or <a href="#" onclick="event.preventDefault()">Browse</a>',
        dictMaxFilesExceeded: null,
        headers: {
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      }
    }
  },
  computed: {
    stepTitle() {
      const titles = {
        1: 'Optix Lab',
        2: 'Upload Data & Generate Report',
        3: 'Generating Report'
      }
      return titles[this.currentStep] || ''
    },
    canGenerateReport() {
      if (this.isVisualPlayBookAnalysis) {
        if (this.inputMode === 'search') {
          return this.searchInput.trim().length > 0 && this.selectedRetailer && this.countResults
        } else if (this.inputMode === 'category') {
          const validCategoryUrls = this.categoryUrls.filter(url => url.trim())
          return validCategoryUrls.length > 0 && this.selectedRetailer && this.countResults
        } else if (this.inputMode === 'products') {
          const validUrls = this.productUrls.filter(url => url.trim())
          return validUrls.length > 0 && validUrls.length <= 5 && this.countResults
        }
        return false
      }
      // Allow generation if we have uploaded files, URL input, OR selected products from lists
      return this.uploadedFiles.length > 0 || this.urlInput.trim().length > 0 || this.selectedProducts.length > 0
    },
    urlCount() {
      if (!this.urlInput) return 0
      return this.urlInput.trim().split(/[\s,\n]+/).filter(Boolean).length
    },
    uploadedFilesCount() {
      return this.uploadedFiles.length
    },
    promptOptions() {
      if (this.customPrompts[0] === 'loading') {
        return []
      }

      let prompts = this.customPrompts

      const filteredPrompts = prompts.filter(prompt => {
        for (const p of prompts) {
          if (
            p.show_variations === false &&
            Array.isArray(p.variations) &&
            p.variations.includes(prompt.id)
          ) {
            return false // remove this prompt
          }
        }
        return true // keep this prompt
      })

      return filteredPrompts.map(prompt => ({
        ...prompt,
        name: this.isPromptAParent(prompt) ? prompt.name : "└─ " + prompt.name
      }))
    },

    $route() {
      return this.$route || { params: { tab: 'files' } }
    },

    isVisualPlayBookAnalysis() {
      return this.selectedPrompt?.analysisFormat?.type === 'visual-playbook'
    },
    productListOptions() {
      return this.availableProductLists || []
    }
  },
  beforeCreate() {
    const mockRoute = {
      params: { tab: 'files' },
      query: {},
      path: '/mock',
      name: 'mock'
    }

    Object.defineProperty(this, '$route', {
      value: mockRoute,
      writable: false,
      enumerable: true
    })

    if (!this.$root.$route) {
      Object.defineProperty(this.$root, '$route', {
        value: mockRoute,
        writable: false,
        enumerable: true
      })
    }
  },
  mounted() {
    this.loadPrompts()
    this.loadAvailableProductLists()
    this.setupPasteListener()

    if (!Vue.mixin.__routeMixinAdded) {
      Vue.mixin({
        beforeCreate() {
          if (!this.$route && this.$options.name !== 'OptixLabAnalyzer') {
            Object.defineProperty(this, '$route', {
              value: { params: { tab: 'files' } },
              writable: false
            })
          }
        }
      })
      Vue.mixin.__routeMixinAdded = true
    }

    this.$nextTick(() => {
      if (this.$refs.uploadContainer) {
        this.$refs.uploadContainer.focus()
      }
    })
  },
  beforeDestroy() {
    document.removeEventListener('paste', this.handlePaste)
  },
  methods: {
    async loadPrompts() {
      this.customPrompts = ['loading']
      try {
        const response = await axios.get('/banner/groups/index_custom_prompts', {
          params: {
            type: 3, // CUSTOM_PROMPT_TYPE_ANALYTICS
            page: 1,
            paginate: false
          }
        })

        this.customPrompts = []
        for (const prompt of response.data) {
          if (!prompt || !prompt.id || !prompt.name || !prompt.prompt_start) {
            console.warn("Invalid prompt data:", prompt)
            continue
          }
          let applyToPrevious = false
          let applyToImageNumber = null
          if (prompt.prompt_start_variations && Array.isArray(prompt.prompt_start_variations)) {
            const variationData = prompt.prompt_start_variations.find(variation => variation.id === String(prompt.id).split('_').pop())
            if (variationData) {
              applyToPrevious = !!variationData.apply_to_previous
              applyToImageNumber = variationData.apply_to_image_number || null
            }
          }
          const analysisFormat = prompt.response_format && prompt.response_format.structuredOutputSchema ? prompt.response_format : null


          this.customPrompts.push({
            id: prompt.id,
            name: prompt.name,
            prompt: prompt.prompt_start
              .replaceAll("[current-date]", new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })),
            is_variation: prompt.is_variation || false,
            variations: prompt.variations || [],
            show_variations: prompt.show_variations !== false,
            analysisFormat: analysisFormat,
            responseFormat: prompt.response_format ? prompt.response_format : null,
            summaryCriteria: prompt.summary_criteria || null,
            model: prompt.model,
            use_openai_websearch: prompt.settings && prompt.settings['use_openai_websearch'] ? true : false,
            applyToPrevious: applyToPrevious,
            applyToImageNumber: applyToImageNumber,
            unpackImagesOpenCV: prompt.settings && prompt.settings['unpack_images_opencv'] ? true : false,
            pdp_title_and_copy_assessment: prompt.settings && prompt.settings['pdp_title_and_copy_assessment'] ? true : false,
            apply_each_variation: prompt.settings && prompt.settings['apply_each_variation'] ? true : false,
            apply_variation_to_each_image: prompt.settings && prompt.settings['apply_variation_to_each_image'] ? true : false
          })
        }

        this.isLoading = false
      } catch (error) {
        console.error('Error loading prompts:', error)
        this.isLoading = false
        toastr.error('Failed to load analysis types')
      }
    },

    async loadAvailableProductLists() {
      this.isLoadingProductLists = true
      try {
        const response = await axios.get('/product-id-list/all')
        this.availableProductLists = response.data.productIdLists || []
      } catch (error) {
        console.error('Failed to load product lists:', error)
      } finally {
        this.isLoadingProductLists = false
      }
    },

    async loadProductsFromLists() {
      if (!this.selectedProductLists || this.selectedProductLists.length === 0) {
        this.availableProducts = []
        this.selectedProducts = []
        return
      }

      this.isLoadingProducts = true
      try {
        const allProducts = []
        for (const productList of this.selectedProductLists) {
          const productListId = productList.id || productList
          const response = await axios.get(`/product-id-list/${productListId}/data`)
          const records = response.data.records || []

          records.forEach(record => {
            const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url
            if (productId) {
              const displayName = this.getProductDisplayName(record)
              allProducts.push({
                ...record,
                id: record.id,
                record_id: record.id,
                product_id: productId,
                display_name: displayName,
                product_list_name: productList.name,
                asin: record.asin,
                tcin: record.tcin,
                walmart_id: record.walmart_id,
                salsify_id: record.salsify_id,
                url: record.url
              })
            }
          })
        }

        this.availableProducts = allProducts
        toastr.success(`Loaded ${allProducts.length} products from ${this.selectedProductLists.length} product list(s)`)
      } catch (error) {
        console.error('Failed to load products from lists:', error)
        toastr.error('Failed to load products from lists')
      } finally {
        this.isLoadingProducts = false
      }
    },

    getProductDisplayName(record) {
      const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url
      let type = ''

      if (record.asin) type = 'ASIN'
      else if (record.tcin) type = 'TCIN'
      else if (record.walmart_id) type = 'Walmart ID'
      else if (record.salsify_id) type = 'Salsify ID'
      else if (record.url) type = 'URL'

      return `${productId} (${type})`
    },

    populateSelectedProducts() {
      if (!this.selectedProducts || this.selectedProducts.length === 0) {
        this.selectedProductRecordIds = []
        return
      }

      this.selectedProductRecordIds = this.selectedProducts.map(product => product.record_id)

      toastr.success(`Selected ${this.selectedProducts.length} product(s) for analysis`)
    },

    selectAll() {
      if (this.selectedProducts.length === this.availableProducts.length) {
        return this.clearAll();
      }
      this.selectedProducts = [...this.availableProducts]
      this.populateSelectedProducts()
    },

    clearAll() {
      this.selectedProducts = []
      this.populateSelectedProducts()
    },

    limitText(count) {
      return `and ${count} more`
    },

    validatePlaybookReport() {
      if (this.inputMode === 'search' && !this.searchInput.trim()) {
        toastr.error("Please provide a search keyword")
        return false
      }

      if (this.inputMode === 'category') {
        const validCategoryUrls = this.categoryUrls.filter(url => url.trim())
        if (validCategoryUrls.length === 0) {
          toastr.error("Please provide at least one category URL")
          return false
        }
      }

      if (this.inputMode === 'products') {
        const validUrls = this.productUrls.filter(url => url.trim())
        if (validUrls.length === 0) {
          toastr.error("Please provide at least one product URL or ASIN")
          return false
        }
        if (validUrls.length > 5) {
          toastr.error("Maximum 5 product URLs allowed")
          return false
        }
      }

      if (!this.countResults) {
        toastr.error("Please provide count results for visual playbook analysis")
        return false
      }

      if ((this.inputMode === 'search' || this.inputMode === 'category') && !this.selectedRetailer) {
        toastr.error("Please select a retailer")
        return false
      }

      return true
    },

    setupPasteListener() {
      document.addEventListener('paste', this.handlePaste)
    },

    handlePaste(event) {
      if (this.currentStep !== 2 || this.activeTab !== 0) {
        return
      }

      const items = event.clipboardData?.items
      if (!items) {
        return
      }

      let imageCount = 0

      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          imageCount++
          const file = item.getAsFile()
          if (file) {
            if (this.$refs.fileDropzone?.dropzone) {
              this.$refs.fileDropzone.dropzone.addFile(file)
            } else {
              this.uploadedFiles.push(file)
            }
          }
        }
      }

      if (imageCount > 0) {
        event.preventDefault()
      }
    },

    onFileAdded(file) {
      this.uploadedFiles.push(file)
    },

    onFileRemoved(file) {
      const index = this.uploadedFiles.findIndex(f => f.name === file.name)
      if (index > -1) {
        this.uploadedFiles.splice(index, 1)
      }
    },

    async useSampleImages() {
      try {
        this.isLoading = true
        this.loadingMessage = 'Loading sample image...'

        const response = await fetch('/api/placeholder/800/600')
        const blob = await response.blob()

        const sampleFile = new File([blob], 'sample-image.jpg', {
          type: 'image/jpeg'
        })

        if (this.$refs.fileDropzone && this.$refs.fileDropzone.dropzone) {
          this.$refs.fileDropzone.dropzone.addFile(sampleFile)
        }

        this.activeTab = 0

        this.isLoading = false
        toastr.success('Sample images loaded')
      } catch (error) {
        console.error('Error loading sample images:', error)
        this.isLoading = false
      }
    },

    openSelectFolderModal(mode = 'default') {
      this.selectFolderMode = mode

      if (this.$store.hasModule('file-upload-images')) {
        this.$store.commit('file-upload-images/SET_TAB', 'files')
      }

      this.$bvModal.show('select-folder-modal')
    },

    async fileBrowserItemSelected(items) {
      if (!items || items.length === 0) return

      try {
        this.isLoadingFileBrowserFiles = true

        for (const item of items) {
          try {
            const response = await fetch(
              `${window.location.origin}/share?file=${item.original_path}`
            )

            if (!response.ok) {
              throw new Error(`Failed to fetch file: ${response.statusText}`)
            }

            const blob = await response.blob()

            const file = new File([blob], item.name, {
              type: this.getFileType(item.name),
            })

            if (this.$refs.fileDropzone && this.$refs.fileDropzone.dropzone) {
              this.$refs.fileDropzone.dropzone.addFile(file)
            }
          } catch (error) {
            console.error("Error processing file from browser:", error)
            toastr.error(`Failed to process file: ${item.name}`)
          }
        }

        this.activeTab = 0

        this.isLoadingFileBrowserFiles = false
        toastr.success(`Successfully loaded ${items.length} file(s) from browser`)
      } catch (error) {
        console.error('Error loading files from browser:', error)
        this.isLoadingFileBrowserFiles = false
        toastr.error('Failed to load files from browser')
      }
    },

    getFileType(filename) {
      const extension = filename.split('.').pop().toLowerCase()
      const typeMap = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'webp': 'image/webp'
      }
      return typeMap[extension] || 'image/jpeg'
    },

    async usePreviousImages(getUrl = 0) {
      try {
        this.isLoadingPreviousImages = true

        const { data } = await axios.get('/analysis-report/last-used-images', {params: {get_url: getUrl}})

        if (!data || data.status === 'error') {
          toastr.error(data?.message || 'Failed to load previous images')
          return
        }

        if (getUrl) {
          if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
            this.urlInput = data.urls.join('\n')
            this.activeTab = 1 // Switch to URLs tab
            toastr.success('Previous URLs loaded successfully')
          } else if (data.url) {
            this.urlInput = data.url
            this.activeTab = 1 // Switch to URLs tab
            toastr.success('Previous URL loaded successfully')
          } else {
            toastr.info('No previous URLs found')
          }
          return
        }

        const images = data.images || []

        if (data.manual_item_url) {
          const urlCount = (data.manual_item_url.match(/\n/g) || []).length + 1
          if (urlCount > 1) {
            this.urlInput = data.manual_item_url
            toastr.info(`Loaded ${urlCount} URLs and their corresponding images from previous reports`)
          } else {
            this.urlInput = data.manual_item_url
          }
        } else if (data.item_url) {
          this.urlInput = data.item_url
        }

        if (images.length === 0) {
          toastr.warning('No previous images found')
          return
        }

        if (this.$refs.fileDropzone?.dropzone) {
          this.$refs.fileDropzone.dropzone.removeAllFiles(true)
        }

        this.uploadedFiles = []

        for (const [index, imageData] of images.entries()) {
          try {
            const filename = imageData.name || `previous-image-${Date.now()}-${index}.jpg`
            const base64Image = imageData.image || imageData
            const file = dataURLtoFile(base64Image, filename)

            if (this.$refs.fileDropzone?.dropzone) {
              this.$refs.fileDropzone.dropzone.addFile(file)
            }
          } catch (error) {
            console.error('Error processing base64 image:', error)
          }
        }

        this.activeTab = 0

        toastr.success(`Added ${images.length} previous images`)
      } catch (error) {
        console.error('Error fetching previous images:', error)
        toastr.error(error.response?.data?.message || 'Failed to load previous images')
      } finally {
        this.isLoadingPreviousImages = false
      }
    },

    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },

    debugGenerateReport() {
      this.generateReport()
    },

    async generateReport() {
      try {
        this.isGenerating = true
        this.currentStep = 3
        this.generationStep = 1
        this.generationStatus = 'Processing Files...'
        this.generationMessage = 'Uploading and processing your images'

        const uploadedFilesBackup = [...this.uploadedFiles]

        if (!this.selectedPrompt) {
          throw new Error("Please select an analysis prompt")
        }

        if (this.selectedPrompt?.analysisFormat?.type === 'gap-analysis-summary') {
          return await this.processGapAnalysisSummary()
        }

        if (this.isVisualPlayBookAnalysis) {
          return await this.processVisualPlayBookAnalysisResponse()
        }

        await this.sleep(1000)

        this.generationStep = 2
        this.generationStatus = 'AI Analysis in Progress...'
        this.generationMessage = 'Our AI is analyzing your content'

        let processedUrls = []
        let allFiles = []
        let webScrapeResponseHtml = null

        let urls = []
        if (this.urlInput && this.urlInput.trim()) {
          urls = this.urlInput.trim().split(/[\s,\n]+/).filter(Boolean)
        }

        if (this.selectedProducts.length > 0) {
          const productUrls = this.selectedProducts.map(product => {
            return product.asin ? `https://www.amazon.com/dp/${product.asin}` :
                   product.tcin ? `https://www.target.com/p/-/${product.tcin}` :
                   product.walmart_id ? `https://www.walmart.com/ip/${product.walmart_id}` :
                   product.url || ''
          }).filter(url => url.trim())

          urls.push(...productUrls)
        }

        if (urls.length > 0) {

          if (urls.length > 1) {
            return await this.processMultipleUrlsSeparately(urls, uploadedFilesBackup, this.selectedProductRecordIds)
          }

          for (const inputUrl of urls) {
            let url = inputUrl.trim()

            if (/^[A-Z0-9]{10}$/i.test(url)) {
              url = `https://www.amazon.com/dp/${url}`
            }
            else if (/^A-\d{8,9}$/i.test(url)) {
              url = `https://www.target.com/p/-/${url.toUpperCase()}`
            }

            processedUrls.push(url)

            try {
              const { data } = await axios.post('/webscrape', { url })

              if (data && data.response_html) {
                webScrapeResponseHtml = data.response_html
              }
              if (data && data.images && data.images.length > 0) {
                for (const imageUrl of data.images) {
                  try {
                    const { highRes, lowRes } = await this.downloadImageWithBothQualities(imageUrl)
                    allFiles.push({
                      type: 'image_url',
                      image_url: {
                        url: lowRes,
                        highResUrl: highRes
                      }
                    })
                  } catch (error) {
                    console.error('Error processing image:', error)
                    allFiles.push({
                      type: 'image_url',
                      image_url: {
                        url: imageUrl,
                        highResUrl: imageUrl
                      }
                    })
                  }
                }
                toastr.success(`Successfully processed URL: ${data.images.length} images found`)
              } else {
                toastr.warning(`No images found at URL: ${url}`)
              }
            } catch (error) {
              console.error('Error scraping URL:', error)
              toastr.error(`Failed to process URL: ${url}`)
            }
          }
        }

        if (uploadedFilesBackup && uploadedFilesBackup.length > 0) {
          for (const file of uploadedFilesBackup) {
            const dataURL = await this.fileToDataURL(file)
            allFiles.push({
              type: 'image_url',
              image_url: {
                url: dataURL,
                highResUrl: dataURL
              }
            })
          }
        }

        if (allFiles.length === 0 && processedUrls.length === 0 && this.selectedProducts.length === 0) {
          throw new Error("Please provide at least one image, a valid URL, or select products from a product list for analysis")
        }

        await this.sleep(1500)

        this.generationStep = 3
        this.generationStatus = 'Generating Report...'
        this.generationMessage = 'Creating your comprehensive analysis report'

        let prompt = this.selectedPrompt.prompt
          .replaceAll('{{url}}', processedUrls.join(', '))
          .replaceAll('{{urls}}', processedUrls.join(', '))

        const imageContents = allFiles.map(image => ({
          type: 'image_url',
          image_url: {
            url: image.image_url.url,
            highResUrl: image.image_url.highResUrl || image.image_url.url
          }
        }))

        let apiSettings = {
          messages: [{
            role: "user",
            content: [
              {
                type: "text",
                text: prompt,
              },
              ...imageContents,
            ],
          }],
          model: this.selectedPrompt.model,
        }

        if (this.selectedPrompt.use_openai_websearch) {
          apiSettings.websearch = true
        }

        if (this.selectedPrompt.responseFormat?.structuredOutputSchema) {
          apiSettings.response_format = this.selectedPrompt.responseFormat.structuredOutputSchema
        }

        const responseMessage = await this.callOpenAI(apiSettings)

        if (!responseMessage || !responseMessage.content) {
          throw new Error("Empty response from analysis service")
        }

        await this.sleep(500)

        await this.processAnalysisResponse(responseMessage, apiSettings, imageContents, processedUrls, webScrapeResponseHtml)

      } catch (error) {
        console.error('Error generating report:', error)
        this.isGenerating = false
        this.currentStep = 2
        toastr.error(error.message || 'Failed to generate report')
      }
    },

    isPromptAParent(aPrompt) {
      return !aPrompt.is_variation
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    cancelGeneration() {
      this.isGenerating = false
      this.currentStep = 2
      this.generationStep = 1
      this.generationStatus = 'Processing your request...'
      this.generationMessage = 'Please wait while we prepare your analysis'

      toastr.warning('Report generation cancelled')
    },

    async processGapAnalysisSummary() {

      const urls = this.urlInput ? this.urlInput.trim().split(/[\s,\n]+/).filter(Boolean) : []

      let allFiles = []
      if (this.uploadedFiles && this.uploadedFiles.length > 0) {
        for (const file of this.uploadedFiles) {
          const dataURL = await this.fileToDataURL(file)
          allFiles.push({
            type: 'image_url',
            image_url: {
              url: dataURL,
              highResUrl: dataURL
            }
          })
        }
      }

      if (urls.length === 0 && allFiles.length === 0) {
        throw new Error("Please provide at least one image or a valid URL for analysis")
      }

      const imageContents = allFiles.map(image => ({
        type: 'image_url',
        image_url: {
          url: image.image_url.url,
          highResUrl: image.image_url.highResUrl || image.image_url.url
        }
      }))


      const data = {
        imageContents: imageContents,
        allFiles: allFiles
      }

      if (urls.length > 0) {
        data.manual_item_url = this.urlInput.trim()
        if (urls[0]) {
          data.item_url = urls[0]
        }
      }

      const requestData = {
        'api_response': {},
        'api_call_data': {},
        'webscrape_response_html': null,
        'type': this.selectedPrompt?.analysisFormat?.type,
        'data': data,
        'from_url': urls.length > 0,
        'product_urls': urls,
        'prompt': this.selectedPrompt.prompt,
        'summary_criteria': this.selectedPrompt.summaryCriteria,
        'response_format': this.selectedPrompt.responseFormat,
        'product_list_id': null
      }

      if (this.selectedProductRecordIds && this.selectedProductRecordIds.length > 0) {
        requestData.product_id_list_records_id = this.selectedProductRecordIds[0]
      }

      const analysisResponse = await axios.post('/banner/groups/0/analysis-reports', requestData)

      this.redirectToAnalyticsHistory(analysisResponse.data.analytics_history_id)
    },

    async processVisualPlayBookAnalysisResponse() {
      if (!this.validatePlaybookReport()) {
        return
      }

      this.generationStep = 1
      this.generationStatus = 'Collecting Product Images...'
      this.generationMessage = 'This may take a while depending on the number of product images'

      let requestData = {
        count_results: this.countResults,
        max_images: this.maxImagesPerProduct
      }

      if ((this.inputMode === 'search' || this.inputMode === 'category') && this.selectedRetailer) {
        requestData.retailer = this.selectedRetailer
      }

      if (this.inputMode === 'search') {
        requestData.keyword = this.searchInput
      } else if (this.inputMode === 'category') {
        const validCategoryUrls = this.categoryUrls.filter(url => url.trim())
        requestData.category_urls = validCategoryUrls
      } else if (this.inputMode === 'products') {
        const processedUrls = this.productUrls
          .filter(url => url.trim())
          .map(url => this.processURLInput(url.trim()))
        requestData.product_urls = processedUrls
      }

      let data
      try {
        const response = await axios.post('/search-scrape', requestData)
        data = response.data
      } catch (error) {
        console.error('Image crawling failed:', error)
        this.isGenerating = false
        this.generationStatus = 'Error'
        this.generationMessage = 'Cannot find any images'
        throw new Error('Cannot find any images')
      }

      let responses = []
      const totalImages = Object.keys(data).length
      if (Object.keys(data).length > 0) {
        let maxCountImages = 0
        let allImages = []
        let allProductLinks = []
        let i = 1

        for (const key in data) {
          let url = key
          let prompt = this.selectedPrompt.prompt.replaceAll('[iterated-url]', `${url} ${data[key]['images'].length} images`)
          let imageContents = []
          if (data[key]['images'].length > maxCountImages) {
            maxCountImages = data[key]['images'].length
          }
          let highResImages = []

          for (const item of data[key]['images']) {
            const { highRes, lowRes } = await this.downloadImageWithBothQualities(item)
            highResImages.push(highRes)
            imageContents.push({
              type: 'image_url',
              image_url: {
                url: highRes
              }
            })
          }
          allImages.push(highResImages)
          allProductLinks.push(url)

          let apiSettings = {
            messages: [
              {
                role: 'user',
                content: [
                  {
                    type: 'text',
                    text: prompt
                  },
                  ...imageContents
                ]
              }
            ],
            model: this.selectedPrompt.model,
            response_format: this.selectedPrompt.responseFormat.structuredOutputSchema
          }

          this.generationStep = 2
          this.generationStatus = `Analyzing image ${i} of ${totalImages}...`
          this.generationMessage = 'Processing images to understand layout, design elements, and visual patterns'

          let responseMessage = await this.callOpenAI(apiSettings)

          if (!responseMessage || !responseMessage.content) {
            throw new Error('Empty response from analysis service')
          }

          responses.push(responseMessage.content)
          i++
        }

        let prompt = `Consolidate the ${maxCountImages} images from each of the image carousels from the following products (that are best practice examples) into one single playbook with best practices for each of the ${maxCountImages} images.\n\n` +
          responses.map((response, index) => `Analyzed product ${index + 1}:\n${response}`).join('\n\n')

        let apiSettings = {
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: prompt
                }
              ]
            }
          ],
          model: this.selectedPrompt.model,
          response_format: this.selectedPrompt.responseFormat.structuredOutputSchema
        }

        this.generationStep = 3
        this.generationStatus = 'Creating the Visual Playbook...'
        this.generationMessage = 'Combining insights from all analyzed images to build a consolidated best practices guide'

        let responseMessage = await this.callOpenAI(apiSettings)

        if (!responseMessage || !responseMessage.content) {
          throw new Error('Empty response from analysis service')
        }

        let analysisResult = JSON.parse(responseMessage.content)
        analysisResult['images'] = allImages
        analysisResult['details'] = {
          'retailer': this.selectedRetailer,
          'search_term': this.inputMode === 'search' ? this.searchInput : null,
          'category_urls': this.inputMode === 'category' ? this.categoryUrls.filter(url => url.trim()) : null,
          'product_urls': this.inputMode === 'products' ? this.productUrls.filter(url => url.trim()) : null,
          'input_mode': this.inputMode,
          'date': new Date().toISOString()
        }
        analysisResult.title = this.selectedPrompt.name
        analysisResult.productLinks = allProductLinks
        analysisResult['selectedPromptId'] = this.selectedPrompt.id

        this.generationStep = 4
        this.generationStatus = 'Saving Results...'
        this.generationMessage = 'Almost done. Finalizing and saving your visual playbook'

        const requestData = {
          'api_response': responses,
          'api_call_data': JSON.stringify(apiSettings),
          'webscrape_response_html': null,
          'type': this.selectedPrompt?.analysisFormat?.type || 'visual-playbook',
          'data': analysisResult,
          'from_url': null
        }

        if (this.selectedProductRecordIds && this.selectedProductRecordIds.length > 0) {
          requestData.product_id_list_records_id = this.selectedProductRecordIds[0]
        }

        const analysisResponse = await axios.post('/banner/groups/0/analysis-reports', requestData)

        this.redirectToAnalyticsHistory(analysisResponse.data.analytics_history_id)
      }
    },

    async processMultipleUrlsSeparately(urls, uploadedFiles, productRecordIds = []) {
      try {
        this.generationStep = 2
        this.generationStatus = `Processing ${urls.length} URLs separately...`
        this.generationMessage = 'Creating individual reports for each URL'

        const batchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        const reportIds = []
        const createdReports = []

        for (let i = 0; i < urls.length; i++) {
          const url = urls[i].trim()
          this.generationStatus = `Processing URL ${i + 1} of ${urls.length}...`
          this.generationMessage = `Creating report for: ${url}`

          const productRecordId = productRecordIds.length > 0 ?
                                 productRecordIds[i % productRecordIds.length] : null

          try {
            const report = await this.createSingleUrlReport(url, `Report ${i + 1} - ${url}`, batchId, uploadedFiles, productRecordId)

            if (report && report.analyticsHistoryId) {
              reportIds.push(report.analyticsHistoryId)
              report.originalUrl = url
              createdReports.push(report)
            }
          } catch (error) {
            console.error(`Error processing URL ${url}:`, error)
            toastr.error(`Failed to process URL: ${url}`)
          }
        }

        if (reportIds.length > 0) {
          toastr.success(`Successfully created ${reportIds.length} reports`)
          this.redirectToAnalyticsHistory(reportIds)
        } else {
          throw new Error('No reports were created successfully')
        }
      } catch (error) {
        console.error('Error processing multiple URLs:', error)
        throw error
      }
    },

    async createSingleUrlReport(url, reportTitle, batchId, uploadedFiles, productRecordId = null) {
      const recordId = productRecordId

      if (!this.selectedPrompt) {
        throw new Error('No prompt selected')
      }

      let allFiles = []
      let processedUrls = []
      let webScrapeResponseHtml = null

      let processedUrl = url

      if (/^[A-Z0-9]{10}$/i.test(url)) {
        processedUrl = `https://www.amazon.com/dp/${url}`
      }
      else if (/^A-\d{8,9}$/i.test(url)) {
        processedUrl = `https://www.target.com/p/-/${url.toUpperCase()}`
      }

      processedUrls.push(processedUrl)

      try {
        const { data } = await axios.post('/webscrape', { url: processedUrl })

        if (data && data.response_html) {
          webScrapeResponseHtml = data.response_html
        }
        if (data && data.images && data.images.length > 0) {
          for (const imageUrl of data.images) {
            try {
              const { highRes, lowRes } = await this.downloadImageWithBothQualities(imageUrl)
              allFiles.push({
                type: 'image_url',
                image_url: {
                  url: lowRes,
                  highResUrl: highRes 
                }
              })
            } catch (error) {
              console.error('Error processing image:', error)
              allFiles.push({
                type: 'image_url',
                image_url: {
                  url: imageUrl,
                  highResUrl: imageUrl
                }
              })
            }
          }
        }
      } catch (error) {
        console.error('Error scraping URL:', error)
        toastr.error(`Failed to process URL: ${processedUrl}`)
      }

      if (uploadedFiles && uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          const dataURL = await this.fileToDataURL(file)
          allFiles.push({
            type: 'image_url',
            image_url: {
              url: dataURL,
              highResUrl: dataURL
            }
          })
        }
      }

      if (allFiles.length === 0) {
        throw new Error(`No images found for URL: ${processedUrl}`)
      }

      let prompt = this.selectedPrompt.prompt
        .replaceAll('{{url}}', processedUrl)
        .replaceAll('{{urls}}', processedUrl)

      const imageContents = allFiles.map(image => ({
        type: 'image_url',
        image_url: {
          url: image.image_url.url,
          highResUrl: image.image_url.highResUrl || image.image_url.url
        }
      }))

      let apiSettings = {
        messages: [{
          role: "user",
          content: [
            {
              type: "text",
              text: prompt,
            },
            ...imageContents,
          ],
        }],
        model: this.selectedPrompt.model,
      }

      if (this.selectedPrompt.use_openai_websearch) {
        apiSettings.websearch = true
      }

      if (this.selectedPrompt.responseFormat?.structuredOutputSchema) {
        apiSettings.response_format = this.selectedPrompt.responseFormat.structuredOutputSchema
      }

      const responseMessage = await this.callOpenAI(apiSettings)

      if (!responseMessage || !responseMessage.content) {
        throw new Error("Empty response from analysis service")
      }

      return await this.processSingleUrlAnalysisResponse(responseMessage, apiSettings, imageContents, processedUrls, webScrapeResponseHtml, batchId, url, recordId)
    },

    async processSingleUrlAnalysisResponse(responseMessage, apiSettings, imageContents, processedUrls, webScrapeResponseHtml, batchId, originalUrl, productRecordId = null) {
      try {
        let analysisResult
        const content = responseMessage.content
        const isJsonResponse = this.isValidJSON(content)

        if (isJsonResponse) {
          try {
            const parsedResponse = JSON.parse(content)
            if (parsedResponse.error) {
              throw new Error(`API error: ${parsedResponse.error}`)
            }
            analysisResult = parsedResponse.properties || parsedResponse
          } catch (error) {
            console.error("Error parsing JSON response:", error)
            throw new Error("Invalid JSON response")
          }
        } else {
          analysisResult = {
            markdown: content
          }
        }

        const apiResponseForStorage = isJsonResponse ? content : JSON.stringify({ content: content })

        analysisResult.images = imageContents.map(image => image.image_url.highResUrl || image.image_url.url)
        analysisResult.reportType = this.selectedPrompt.name
        analysisResult.selectedPromptId = this.selectedPrompt.id
        analysisResult.title = this.selectedPrompt.name
        analysisResult.batch_id = batchId

        analysisResult.manual_item_url = originalUrl
        analysisResult.item_url = processedUrls[0]

        let analysisType = 'generic-markdown'
        if (isJsonResponse && this.selectedPrompt?.analysisFormat?.type) {
          analysisType = this.selectedPrompt.analysisFormat.type
        }

        const requestData = {
          'api_response': apiResponseForStorage,
          'api_call_data': JSON.stringify(apiSettings),
          'webscrape_response_html': webScrapeResponseHtml,
          'type': analysisType,
          'data': analysisResult,
          'from_url': processedUrls.length > 0,
          'product_urls': processedUrls,
          'prompt': this.selectedPrompt.prompt,
          'summary_criteria': this.selectedPrompt.summaryCriteria,
          'response_format': this.selectedPrompt.responseFormat
        }

        if (productRecordId) {
          requestData.product_id_list_records_id = productRecordId
        }

        const analysisResponse = await axios.post('/banner/groups/0/analysis-reports', requestData)

        return {
          analyticsHistoryId: analysisResponse.data.analytics_history_id,
          result: analysisResponse.data.data
        }

      } catch (error) {
        console.error('Error processing single URL analysis response:', error)
        throw error
      }
    },

    async callOpenAI(settings) {
      try {
        const response = await axios.post('/call-open-ai', this.prepareApiRequest(settings))

        if (response.data && typeof response.data === "object") {
          return response.data
        } else {
          console.warn("Unexpected API response format:", response)
          throw new Error("Invalid API response format")
        }
      } catch (error) {
        if (error.name === "AbortError") {
          throw new Error("API request timed out")
        }

        const errorMessage =
          error.response?.data?.error?.message ||
          error.message ||
          "Unknown API error"

        throw new Error(errorMessage)
      }
    },

    prepareApiRequest(settings) {
      if (settings.messages && Array.isArray(settings.messages) && Array.isArray(settings.messages[0]?.content)) {
        settings.messages[0].content.forEach(item => {
          if (item.type === 'text') {
            item.text = item.text.replaceAll("[images_count]", settings.messages[0].content.filter(m => m.type === 'image_url').length);
          }
        })
      }

      return settings;
    },

    fileToDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    async processAnalysisResponse(responseMessage, apiSettings, imageContents, processedUrls, webScrapeResponseHtml) {
      try {
        let analysisResult
        const content = responseMessage.content
        const isJsonResponse = this.isValidJSON(content)

        if (isJsonResponse) {
          try {
            const parsedResponse = JSON.parse(content)
            if (parsedResponse.error) {
              throw new Error(`API error: ${parsedResponse.error}`)
            }
            analysisResult = parsedResponse.properties || parsedResponse
          } catch (error) {
            console.error("Error parsing JSON response:", error)
            throw new Error("Invalid JSON response")
          }
        } else {
          analysisResult = {
            markdown: content
          }
        }

        const apiResponseForStorage = isJsonResponse ? content : JSON.stringify({ content: content })

        analysisResult.images = imageContents.map(image => image.image_url.highResUrl || image.image_url.url)
        analysisResult.reportType = this.selectedPrompt.name
        analysisResult.selectedPromptId = this.selectedPrompt.id
        analysisResult.title = this.selectedPrompt.name

        if (processedUrls.length > 0) {
          analysisResult.manual_item_url = this.urlInput.trim()

          if (processedUrls[0]) {
            analysisResult.item_url = processedUrls[0]
          }
        }

        let analysisType = 'generic-markdown'
        if (isJsonResponse && this.selectedPrompt?.analysisFormat?.type) {
          analysisType = this.selectedPrompt.analysisFormat.type
        }

        const requestData = {
          'api_response': apiResponseForStorage,
          'api_call_data': JSON.stringify(apiSettings),
          'webscrape_response_html': webScrapeResponseHtml,
          'type': analysisType,
          'data': analysisResult,
          'from_url': processedUrls.length > 0,
          'product_urls': processedUrls,
          'prompt': this.selectedPrompt.prompt,
          'summary_criteria': this.selectedPrompt.summaryCriteria,
          'response_format': this.selectedPrompt.responseFormat
        }

        if (this.selectedProductRecordIds && this.selectedProductRecordIds.length > 0) {
          requestData.product_id_list_records_id = this.selectedProductRecordIds[0]
        }

        const analysisResponse = await axios.post('/banner/groups/0/analysis-reports', requestData)

        this.redirectToAnalyticsHistory(analysisResponse.data.analytics_history_id)

      } catch (error) {
        console.error('Error processing analysis response:', error)
        throw error
      }
    },

    isValidJSON(str) {
      try {
        JSON.parse(str)
        return true
      } catch (e) {
        return false
      }
    },

    redirectToAnalyticsHistory(reportIds = null) {
      this.isGenerating = false
      toastr.success('Report(s) generated successfully!')

      if (reportIds) {
        if (Array.isArray(reportIds)) {
          if (reportIds.length > 0) {
            window.location.href = `/optix?filter_report_ids=${reportIds.join(',')}`
          } else {
            window.location.href = '/optix#reports'
          }
        } else {
          window.location.href = `/optix?filter_report_id=${reportIds}#reports`
        }
      } else {
        window.location.href = '/optix#reports'
      }
    },

    async downloadImageWithBothQualities(imageUrl) {
      try {
        const response = await fetch(imageUrl)
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`)
        }

        const blob = await response.blob()

        const highRes = await this.blobToDataURL(blob)

        const lowRes = await this.compressImage(blob, 0.7, 800) // 70% quality, max 800px width

        return { highRes, lowRes }
      } catch (error) {
        console.error('Error downloading image:', error)
        return { highRes: imageUrl, lowRes: imageUrl }
      }
    },

    blobToDataURL(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    },

    async compressImage(blob, quality = 0.7, maxWidth = 800) {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()

        img.onload = () => {
          let { width, height } = img
          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }

          canvas.width = width
          canvas.height = height

          ctx.drawImage(img, 0, 0, width, height)

          const compressedDataURL = canvas.toDataURL('image/jpeg', quality)
          resolve(compressedDataURL)
        }

        img.onerror = () => {
          this.blobToDataURL(blob).then(resolve)
        }

        img.src = URL.createObjectURL(blob)
      })
    },

    handleProductUrlsInput() {
      this.isUpdatingFromTextarea = true

      if (!this.productUrlsText || this.productUrlsText.trim() === '') {
        this.productUrls = ['']
        this.allProductUrls = []
        this.$nextTick(() => {
          this.isUpdatingFromTextarea = false
        })
        return
      }

      const lines = this.productUrlsText.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)

      const processedLines = []
      for (const line of lines) {
        const processedLine = this.processURLInput(line)
        if (processedLine) {
          processedLines.push(processedLine)
        }
      }

      const processedText = processedLines.join('\n')
      if (processedText !== lines.join('\n')) {
        this.productUrlsText = processedText
      }

      this.allProductUrls = processedLines
      this.productUrls = processedLines.slice(0, 5)

      if (this.productUrls.length === 0) {
        this.productUrls = ['']
        this.allProductUrls = []
      }

      this.$nextTick(() => {
        this.isUpdatingFromTextarea = false
      })
    },

    handleProductUrlsPaste() {
      setTimeout(() => {
        const currentLines = this.productUrlsText.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)

        if (currentLines.length === 0) {
          return
        }

        const textarea = this.$refs.productUrlsTextarea
        if (textarea && textarea.$el) {
          const textareaElement = textarea.$el.querySelector('textarea') || textarea.$el
          const cursorPosition = textareaElement.selectionStart
          const currentText = this.productUrlsText

          if (cursorPosition === currentText.length && currentText.length > 0 && !currentText.endsWith('\n')) {
            this.productUrlsText = currentText + '\n'

            this.$nextTick(() => {
              textareaElement.setSelectionRange(this.productUrlsText.length, this.productUrlsText.length)
            })
          } else if (cursorPosition < currentText.length) {
            const textBefore = currentText.substring(0, cursorPosition)
            const textAfter = currentText.substring(cursorPosition)

            this.productUrlsText = textBefore + '\n' + textAfter

            this.$nextTick(() => {
              const newPosition = textBefore.length + 1
              textareaElement.setSelectionRange(newPosition, newPosition)
            })
          }
        }
      }, 50)
    },

    handleCategoryUrlsInput() {
      this.isUpdatingCategoryFromTextarea = true

      if (!this.categoryUrlsText || this.categoryUrlsText.trim() === '') {
        this.categoryUrls = ['']
        this.$nextTick(() => {
          this.isUpdatingCategoryFromTextarea = false
        })
        return
      }

      const lines = this.categoryUrlsText.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)

      this.categoryUrls = lines

      this.$nextTick(() => {
        this.isUpdatingCategoryFromTextarea = false
      })
    },

    handleCategoryUrlsPaste() {
      setTimeout(() => {

        const textarea = this.$refs.categoryUrlsTextarea
        if (textarea && textarea.$el) {
          const textareaElement = textarea.$el.querySelector('textarea') || textarea.$el
          const cursorPosition = textareaElement.selectionStart
          const currentText = this.categoryUrlsText

          if (cursorPosition === currentText.length && currentText.length > 0 && !currentText.endsWith('\n')) {
            this.categoryUrlsText = currentText + '\n'

            this.$nextTick(() => {
              textareaElement.setSelectionRange(this.categoryUrlsText.length, this.categoryUrlsText.length)
            })
          } else if (cursorPosition < currentText.length) {
            const textBefore = currentText.substring(0, cursorPosition)
            const textAfter = currentText.substring(cursorPosition)

            this.categoryUrlsText = textBefore + '\n' + textAfter

            this.$nextTick(() => {
              const newPosition = textBefore.length + 1
              textareaElement.setSelectionRange(newPosition, newPosition)
            })
          }
        }
      }, 50)
    },

    processURLInput(url) {
      if (!url || !url.trim()) return null

      let processedUrl = url.trim()

      if (/^[A-Z0-9]{10}$/i.test(processedUrl)) {
        return `https://www.amazon.com/dp/${processedUrl}`
      }
      else if (/^A-\d{8,9}$/i.test(processedUrl)) {
        return `https://www.target.com/p/-/${processedUrl.toUpperCase()}`
      }

      return processedUrl
    }
  }
}
</script>

<style scoped>
.optix-lab-analyzer {
  padding: 20px 0;
}

.step-progress-bar {
  padding: 15px 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.step-indicators {
  display: flex;
  gap: 10px;
}

.step-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #dee2e6;
  transition: all 0.3s;
}

.step-dot.active {
  background: #007bff;
  transform: scale(1.2);
}

.step-dot.completed {
  background: #28a745;
}

.step-content {
  padding: 20px;
  min-height: 400px;
}

.upload-container {
  border: 2px solid #eaeaea;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  background: white;
}

.upload-container:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.paste-instruction {
  background-color: #f8f9fa;
  border: 2px dashed #ccc;
  border-radius: 4px;
  padding: 12px;
  text-align: center;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.paste-instruction:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.paste-instruction .b-icon {
  margin-right: 8px;
  vertical-align: middle;
}

.upload-count {
  margin: 10px 0;
  text-align: center;
}

.upload-count .badge {
  font-size: 14px;
  padding: 5px 10px;
}

.quick-start-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.settings-group {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

::v-deep .vue-dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #fafafa;
  min-height: 150px;
  padding: 20px;
  transition: all 0.3s ease;
}

::v-deep .vue-dropzone:hover {
  background: #f0f0f0;
  border-color: #adb5bd;
}

::v-deep .vue-dropzone.dz-drag-hover {
  background: #e8f4f8;
  border-color: #007bff;
}

::v-deep .dz-message {
  font-size: 16px;
  color: #6c757d;
  margin: 2em 0;
}

::v-deep .dz-preview.dz-image-preview {
  margin: 10px;
}

::v-deep .dz-preview .dz-image {
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

::v-deep .dz-preview .dz-details {
  background: rgba(33, 150, 243, 0.8);
  color: white;
}

.generating-spinner {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.report-summary-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
}

.generation-steps {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin: 30px 0;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
  min-width: 120px;
}

.step-item i {
  font-size: 24px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step-item span {
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.step-item.active {
  background: #e3f2fd;
  color: #1976d2;
  transform: scale(1.05);
}

.step-item.active i {
  color: #1976d2;
  animation: bounce 1s infinite;
}

.step-item.completed {
  background: #e8f5e8;
  color: #2e7d2e;
}

.step-item.completed i {
  color: #2e7d2e;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

@media (max-width: 768px) {
  .generation-steps {
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }

  .step-item {
    min-width: 200px;
  }
}

.dropzone-container {
  position: relative;
  transition: all 0.3s ease;
}

.dropzone-container.loading ::v-deep .vue-dropzone {
  filter: blur(3px);
  pointer-events: none;
  opacity: 0.7;
}

.file-browser-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.file-browser-loading-overlay .loading-content {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #495057;
}
</style>
